/**
 * FirebaseTenantService.js
 * Connects to Firestore to retrieve tenant configuration.
 * Supports Local Emulators.
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Shared 'db' is now imported from ./firebase.js

// Determine environment prefix based on build arg
const getEnvPrefix = () => {
    const viteEnv = import.meta.env.VITE_APP_ENV || '';
    return viteEnv === 'staging' ? 'staging_' : '';
};


export const FirebaseTenantService = {
    async resolveTenant(hostname) {
        console.debug(`[FirebaseTenantService] Resolving tenant for host: ${hostname}`);

        // Clean hostname (remove port) for local dev mapping
        let lookupKey = hostname.split(':')[0]; // e.g. "localhost" or "animals.app.com"

        // Sprint 5.1: Tenant Query Param Override (Local Dev/E2E only)
        const params = new URLSearchParams(window.location.search);
        const tenantOverride = params.get('tenant');

        if (tenantOverride && (lookupKey === 'localhost' || lookupKey === '127.0.0.1')) {
            console.debug(`[FirebaseTenantService] 🛠️ Overriding tenant resolution with query param: ${tenantOverride}`);
            lookupKey = tenantOverride;
        }

        // 1. Static Registry Hit (SDLC9 Performance Optimization)
        try {
            // Using absolute-relative path; assuming deployment to Hosting root
            const response = await fetch('/config/theme-registry.json');
            if (response.ok) {
                const registry = await response.json();
                if (registry[lookupKey]) {
                    console.debug(`[FirebaseTenantService] ⚡ Registry HIT for ${lookupKey}`);
                    return registry[lookupKey];
                }
            }
        } catch (e) {
            console.warn('[FirebaseTenantService] Registry unavailable, using Firestore fallback.', e);
        }

        // Critic Rule: Standardize local dev hostnames
        if (lookupKey === '127.0.0.1') {
            lookupKey = 'localhost';
        }

        try {
            // 1. Fetch Tenant Config
            const docRef = doc(db, `${getEnvPrefix()}theme_config`, lookupKey);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const tenantData = docSnap.data();
                console.debug(`[FirebaseTenantService] Found tenant: ${tenantData.name}`);

                // 2. Fetch Profile (if defined)
                let profileData = {};
                if (tenantData.profileId) {
                    try {
                        const profileSnap = await getDoc(doc(db, `${getEnvPrefix()}tenant_profiles`, tenantData.profileId));
                        if (profileSnap.exists()) {
                            profileData = profileSnap.data();
                            console.debug(`[FirebaseTenantService] Merged Profile: ${profileData.name}`);
                        }
                    } catch (e) {
                        console.error("[FirebaseTenantService] Failed to load profile", e);
                    }
                }

                // 3. Fetch Plan (if defined)
                let planData = {};
                const planId = tenantData.planId || profileData.planId;
                if (planId) {
                    try {
                        const planSnap = await getDoc(doc(db, `${getEnvPrefix()}subscription_plans`, planId));
                        if (planSnap.exists()) {
                            planData = planSnap.data();
                            console.debug(`[FirebaseTenantService] Applied Plan: ${planData.name}`);
                        }
                    } catch (e) {
                        console.error("[FirebaseTenantService] Failed to load plan", e);
                    }
                }

                // 4. Merge: Tenant > Profile > Plan > Default
                // We merge features deeply-ish (ignoring nested arrays replacement for now)
                const mergedFeatures = {
                    ...profileData.features,
                    ...(planData.features || {}), // Plan might enforce features
                    ...tenantData.features
                };

                return {
                    ...profileData, // Base settings from profile
                    ...tenantData,  // Tenant overrides profile
                    features: mergedFeatures,
                    plan: planData, // Attach plan details
                    tenantId: tenantData.tenantId,
                    name: tenantData.name
                };

            } else {
                console.warn(`[FirebaseTenantService] ❌ No config found for ${lookupKey}. Using Default.`);
                return this.getFallbackTenant();
            }
        } catch (err) {
            console.error('[FirebaseTenantService] Database Error:', err);
            return this.getFallbackTenant();
        }
    },

    getFallbackTenant() {
        return {
            tenantId: 'default',
            name: 'Platform',
            themeId: 'emotions',
            features: {
                showHero: true,
                enableGenerative: true,
                emojis: [
                    { id: '1', emoji: '🐶', label: 'Happy' },
                    { id: '2', emoji: '🦊', label: 'Cool' },
                    { id: '3', emoji: '🐱', label: 'Silly' },
                    { id: '4', emoji: '🦁', label: 'Brave' }
                ],
                styleOverrides: {
                    primaryColor: '#6b7280'
                }
            }
        };
    },

    async getLibrary(libId) {
        try {
            const docRef = doc(db, `${getEnvPrefix()}emoji_libraries`, libId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return docSnap.data();
            }
        } catch (e) {
            console.error('[FirebaseTenantService] Library Load Failed:', e);
        }
        return null;
    }
};
